import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
  FlatList,
  Linking,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import moment from 'moment';
import debounce from 'lodash.debounce';
import PopUp from '../../components/popUp/PopupView';
import { useIsFocused } from '@react-navigation/native';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const ArchiveManager = ({ navigation, route }) => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;
  const focused = useIsFocused();

  // Initial active tab based on route params or default 'properties'
  const [activeTab, setActiveTab] = useState(route?.params?.tab || 'properties');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('A-Z'); // 'A-Z' or 'Z-A'

  // Data States
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [rawDocs, setRawDocs] = useState([]);

  // Modal PopUp States
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeletePropModal, setShowDeletePropModal] = useState(false);
  const [showUnarchivePropModal, setShowUnarchivePropModal] = useState(false);
  const [showDeleteTenantModal, setShowDeleteTenantModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);

  // 1. Fetch Archived Properties
  const fetchArchivedProperties = () => {
    ajax
      .get(Urls.ArchiveProperties)
      .then(({ data }) => {
        if (data?.status && data?.payload) {
          setProperties(data.payload);
        } else {
          setProperties([]);
        }
      })
      .catch((err) => console.log('error fetch archived properties', err));
  };

  // 2. Fetch Archived Tenants
  const fetchArchivedTenants = () => {
    ajax
      .get(Urls.GET_ARCHIVE_TEANANT)
      .then(({ data }) => {
        if (data?.status && data?.payload) {
          const list = data.payload.map((e) => ({
            id: e?.property_tenant_id,
            name: `${e?.tenant?.first_name || ''} ${e?.tenant?.last_name || ''}`.trim() || 'Tenant',
            email: e?.tenant?.email || '-',
            location: e?.property?.name || '-',
          }));
          setTenants(list);
        } else {
          setTenants([]);
        }
      })
      .catch((err) => console.log('error fetch archived tenants', err));
  };

  // 3. Fetch Archived Documents
  const fetchArchivedDocuments = () => {
    ajax
      .get(Urls.GET_ARCHIVE_DOCUMENTS)
      .then(({ data }) => {
        if (data?.status && data?.data) {
          setRawDocs(data.data);
          const list = data.data.map((e) => ({
            id: e?.id,
            name: e?.file_name || e?.document_name || 'Document',
            location: e?.property?.name || '-',
            date: e?.expiry_date ? moment(e.expiry_date).format('DD/MM/YYYY') : '-',
            url: e?.document_url,
          }));
          setDocuments(list);
        } else {
          setDocuments([]);
        }
      })
      .catch((err) => console.log('error fetch archived documents', err));
  };

  useEffect(() => {
    fetchArchivedProperties();
    fetchArchivedTenants();
    fetchArchivedDocuments();
  }, [focused]);

  // Actions
  const handleUnarchiveProperty = () => {
    if (selectedItem?.id) {
      ajax
        .post(`${Urls.UnarchiveProperties}/${selectedItem.id}`)
        .then(({ data }) => {
          setShowUnarchivePropModal(false);
          setSelectedItem(null);
          if (data?.status) fetchArchivedProperties();
        })
        .catch((err) => {
          setShowUnarchivePropModal(false);
          console.log('error unarchive property', err);
        });
    }
  };

  const handleDeleteProperty = () => {
    if (selectedItem?.id) {
      ajax
        .delete(`${Urls.Properties}/${selectedItem.id}`)
        .then(({ data }) => {
          setShowDeletePropModal(false);
          setSelectedItem(null);
          if (data?.status) fetchArchivedProperties();
        })
        .catch((err) => {
          setShowDeletePropModal(false);
          console.log('error delete property', err);
        });
    }
  };

  const handleDeleteTenant = () => {
    if (selectedItem?.id) {
      ajax
        .delete(`${Urls.DELETE_TENANT}${selectedItem.id}`)
        .then(({ data }) => {
          setShowDeleteTenantModal(false);
          setSelectedItem(null);
          if (data?.status) fetchArchivedTenants();
        })
        .catch((err) => {
          setShowDeleteTenantModal(false);
          console.log('error delete tenant', err);
        });
    }
  };

  const handleDeleteDocument = () => {
    if (selectedItem) {
      const docToDelete = rawDocs.find((record) => record.document_url === selectedItem.url || record.id === selectedItem.id);
      if (docToDelete?.id) {
        ajax
          .delete(`${Urls.DeleteDoc}${docToDelete.id}`, { data: docToDelete })
          .then(({ data }) => {
            setShowDeleteDocModal(false);
            setSelectedItem(null);
            if (data?.status) fetchArchivedDocuments();
          })
          .catch((err) => {
            setShowDeleteDocModal(false);
            console.log('error delete document', err);
          });
      }
    }
  };

  // Helper for search and sorting
  const processData = (arr, nameKey = 'name') => {
    let result = [...arr];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item) => {
        const title = (item[nameKey] || item.name || '').toLowerCase();
        const loc = (item.postcode || item.location || '').toLowerCase();
        return title.includes(q) || loc.includes(q);
      });
    }
    result.sort((a, b) => {
      const valA = (a[nameKey] || a.name || '').toLowerCase();
      const valB = (b[nameKey] || b.name || '').toLowerCase();
      if (sortOrder === 'A-Z') return valA.localeCompare(valB);
      return valB.localeCompare(valA);
    });
    return result;
  };

  const processedProperties = processData(properties, 'name');
  const processedTenants = processData(tenants, 'name');
  const processedDocuments = processData(documents, 'name');

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={false}>
      {/* Breadcrumb Header */}
      <Text style={styles.breadcrumbText}>Settings › Archive Manager</Text>

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>Archive Manager</Text>
      </View>

      {/* Sub-Navigation Tabs matching Figma */}
      <View style={styles.tabsRow}>
        <TouchableOpacity
          style={[styles.tabItem, activeTab === 'properties' && styles.tabItemActive]}
          onPress={() => {
            setActiveTab('properties');
            setSearchQuery('');
          }}
          activeOpacity={0.8}
        >
          <Text style={[styles.tabText, activeTab === 'properties' && styles.tabTextActive]}>Properties</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tabItem, activeTab === 'tenants' && styles.tabItemActive]}
          onPress={() => {
            setActiveTab('tenants');
            setSearchQuery('');
          }}
          activeOpacity={0.8}
        >
          <Text style={[styles.tabText, activeTab === 'tenants' && styles.tabTextActive]}>Tenants</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tabItem, activeTab === 'documents' && styles.tabItemActive]}
          onPress={() => {
            setActiveTab('documents');
            setSearchQuery('');
          }}
          activeOpacity={0.8}
        >
          <Text style={[styles.tabText, activeTab === 'documents' && styles.tabTextActive]}>Documents</Text>
        </TouchableOpacity>
      </View>

      {/* Search & Sort Controls Row */}
      <View style={[styles.controlsRow, isMobile && styles.controlsRowMobile]}>
        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
          <TextInput
            style={styles.searchInput}
            placeholder={
              activeTab === 'properties'
                ? 'Search archived properties...'
                : activeTab === 'tenants'
                ? 'Search tenants...'
                : 'Search document archive...'
            }
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        <TouchableOpacity
          style={styles.sortBtn}
          onPress={() => setSortOrder((prev) => (prev === 'A-Z' ? 'Z-A' : 'A-Z'))}
          activeOpacity={0.8}
        >
          <Text style={styles.sortBtnText}>Sort: {sortOrder}</Text>
          <Feather name="chevron-down" size={14} color="#374151" style={{ marginLeft: 4 }} />
        </TouchableOpacity>
      </View>

      {/* TAB 1: PROPERTIES */}
      {activeTab === 'properties' && (
        <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
          {processedProperties.length ? (
            <>
              {!isMobile && (
                <View style={styles.tableHeaderRow}>
                  <Text style={[styles.headerCell, { flex: 1.5 }]}>PROPERTY</Text>
                  <Text style={styles.headerCell}>LOCATION</Text>
                  <Text style={styles.headerCell}>STATUS</Text>
                  <Text style={[styles.headerCell, { textAlign: 'right' }]}>ACTIONS</Text>
                </View>
              )}

              {processedProperties.map((item, idx) => (
                <View key={item.id || idx} style={[styles.tableRow, isMobile && styles.mobileCard]}>
                  <View style={[styles.titleLeftGroup, isMobile && { marginBottom: 8 }]}>
                    <View style={styles.iconCircle}>
                      <MaterialIcons name="home" size={16} color="#6C2BD9" />
                    </View>
                    <Text style={styles.itemTitle}>{item.name}</Text>
                  </View>

                  <Text style={[styles.dataCell, isMobile && styles.mobileCellText]}>
                    {isMobile && <Text style={styles.mobileLabelText}>Location: </Text>}
                    {item.postcode || '-'}
                  </Text>

                  <View style={[styles.dataCell, isMobile && { marginVertical: 6 }]}>
                    <View style={styles.amberBadge}>
                      <Text style={styles.amberBadgeText}>
                        {item.percentage ? `${Math.round(item.percentage)}% Archived` : '10% Archived'}
                      </Text>
                    </View>
                  </View>

                  <View style={[styles.actionsGroup, isMobile && styles.mobileActionsRow]}>
                    <TouchableOpacity
                      style={styles.actionIconButton}
                      onPress={() => {
                        setSelectedItem(item);
                        setShowUnarchivePropModal(true);
                      }}
                    >
                      <Feather name="archive" size={16} color="#6C2BD9" />
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.actionIconButton}
                      onPress={() => {
                        setSelectedItem(item);
                        setShowDeletePropModal(true);
                      }}
                    >
                      <Feather name="trash-2" size={16} color="#DC2626" />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </>
          ) : (
            <View style={styles.emptyBox}>
              <Feather name="home" size={32} color="#9CA3AF" style={{ marginBottom: 8 }} />
              <Text style={styles.emptyText}>No archived properties found.</Text>
            </View>
          )}
        </View>
      )}

      {/* TAB 2: TENANTS */}
      {activeTab === 'tenants' && (
        <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
          {processedTenants.length ? (
            <>
              {!isMobile && (
                <View style={styles.tableHeaderRow}>
                  <Text style={[styles.headerCell, { flex: 1.2 }]}>TENANT NAME</Text>
                  <Text style={[styles.headerCell, { flex: 1.5 }]}>EMAIL</Text>
                  <Text style={styles.headerCell}>PROPERTY</Text>
                  <Text style={[styles.headerCell, { textAlign: 'right' }]}>ACTIONS</Text>
                </View>
              )}

              {processedTenants.map((item, idx) => (
                <View key={item.id || idx} style={[styles.tableRow, isMobile && styles.mobileCard]}>
                  <View style={[styles.titleLeftGroup, isMobile && { marginBottom: 6 }]}>
                    <View style={styles.iconCircle}>
                      <Feather name="user" size={16} color="#6C2BD9" />
                    </View>
                    <Text style={styles.itemTitle}>{item.name}</Text>
                  </View>

                  <Text style={[styles.dataCell, { flex: 1.5 }, isMobile && styles.mobileCellText]}>
                    {isMobile && <Text style={styles.mobileLabelText}>Email: </Text>}
                    {item.email}
                  </Text>

                  <Text style={[styles.dataCell, isMobile && styles.mobileCellText]}>
                    {isMobile && <Text style={styles.mobileLabelText}>Property: </Text>}
                    {item.location}
                  </Text>

                  <View style={[styles.actionsGroup, isMobile && styles.mobileActionsRow]}>
                    <TouchableOpacity
                      style={styles.actionIconButton}
                      onPress={() => {
                        setSelectedItem(item);
                        setShowDeleteTenantModal(true);
                      }}
                    >
                      <Feather name="trash-2" size={16} color="#DC2626" />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </>
          ) : (
            <View style={styles.emptyBoxFigma}>
              <View style={styles.emptyCircleIcon}>
                <Text style={styles.emptyCircleText}>O</Text>
              </View>
              <Text style={styles.emptyFigmaTitle}>No archived tenants yet</Text>
              <Text style={styles.emptyFigmaDesc}>
                When tenancies complete, they will be archived here securely for audit trail history.
              </Text>
              <TouchableOpacity
                style={styles.viewTenanciesBtn}
                onPress={() => navigation.navigate('Tenants')}
                activeOpacity={0.85}
              >
                <Text style={styles.viewTenanciesBtnText}>View Active Tenancies</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* TAB 3: DOCUMENTS */}
      {activeTab === 'documents' && (
        <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
          {processedDocuments.length ? (
            <>
              {!isMobile && (
                <View style={styles.tableHeaderRow}>
                  <Text style={[styles.headerCell, { flex: 1.5 }]}>DOCUMENT TITLE</Text>
                  <Text style={styles.headerCell}>PROPERTY NAME</Text>
                  <Text style={styles.headerCell}>EXPIRY DATE</Text>
                  <Text style={[styles.headerCell, { textAlign: 'right' }]}>ACTIONS</Text>
                </View>
              )}

              {processedDocuments.map((item, idx) => (
                <View key={item.id || idx} style={[styles.tableRow, isMobile && styles.mobileCard]}>
                  <View style={[styles.titleLeftGroup, isMobile && { marginBottom: 6 }]}>
                    <View style={styles.iconCircle}>
                      <Feather name="file-text" size={16} color="#6C2BD9" />
                    </View>
                    <Text style={styles.itemTitle}>{item.name}</Text>
                  </View>

                  <Text style={[styles.dataCell, isMobile && styles.mobileCellText]}>
                    {isMobile && <Text style={styles.mobileLabelText}>Property: </Text>}
                    {item.location}
                  </Text>

                  <Text style={[styles.dataCell, isMobile && styles.mobileCellText]}>
                    {isMobile && <Text style={styles.mobileLabelText}>Expiry Date: </Text>}
                    {item.date}
                  </Text>

                  <View style={[styles.actionsGroup, isMobile && styles.mobileActionsRow]}>
                    {item.url ? (
                      <TouchableOpacity
                        style={styles.actionIconButton}
                        onPress={() => Linking.openURL(item.url)}
                      >
                        <Feather name="download" size={16} color="#6C2BD9" />
                      </TouchableOpacity>
                    ) : null}

                    <TouchableOpacity
                      style={styles.actionIconButton}
                      onPress={() => {
                        setSelectedItem(item);
                        setShowDeleteDocModal(true);
                      }}
                    >
                      <Feather name="trash-2" size={16} color="#DC2626" />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </>
          ) : (
            <View style={styles.emptyBox}>
              <Feather name="file-text" size={32} color="#9CA3AF" style={{ marginBottom: 8 }} />
              <Text style={styles.emptyText}>No archived documents found.</Text>
            </View>
          )}
        </View>
      )}

      {/* PopUp 1: Unarchive Property */}
      <PopUp
        modalVisible={showUnarchivePropModal}
        titleText={'Unarchive Property?'}
        titleColor={'#6C2BD9'}
        subTitleColor={'black'}
        customSubtitle={
          <View>
            <Text>Are you sure you want to unarchive this property?</Text>
          </View>
        }
        actionButtons={[
          {
            text: 'Unarchive',
            width: 100,
            onPress: handleUnarchiveProperty,
          },
          {
            text: 'Cancel',
            width: 100,
            onPress: () => setShowUnarchivePropModal(false),
          },
        ]}
        closePopup={() => setShowUnarchivePropModal(false)}
      />

      {/* PopUp 2: Delete Property */}
      <PopUp
        modalVisible={showDeletePropModal}
        titleText={'Delete Property?'}
        titleColor={'#6C2BD9'}
        subTitleColor={'black'}
        customSubtitle={
          <View>
            <Text>Are you sure you want to delete this property permanently? (Cannot be undone.)</Text>
          </View>
        }
        actionButtons={[
          {
            text: 'Delete',
            width: 100,
            onPress: handleDeleteProperty,
          },
          {
            text: 'Cancel',
            width: 100,
            onPress: () => setShowDeletePropModal(false),
          },
        ]}
        closePopup={() => setShowDeletePropModal(false)}
      />

      {/* PopUp 3: Delete Tenant */}
      <PopUp
        modalVisible={showDeleteTenantModal}
        titleText={'Delete Tenant?'}
        titleColor={'#6C2BD9'}
        subTitleColor={'black'}
        customSubtitle={
          <View>
            <Text>Are you sure you want to delete this tenant record permanently?</Text>
          </View>
        }
        actionButtons={[
          {
            text: 'Delete',
            width: 100,
            onPress: handleDeleteTenant,
          },
          {
            text: 'Cancel',
            width: 100,
            onPress: () => setShowDeleteTenantModal(false),
          },
        ]}
        closePopup={() => setShowDeleteTenantModal(false)}
      />

      {/* PopUp 4: Delete Document */}
      <PopUp
        modalVisible={showDeleteDocModal}
        titleText={'Delete Document?'}
        titleColor={'#6C2BD9'}
        subTitleColor={'black'}
        customSubtitle={
          <View>
            <Text>Are you sure you want to delete this document permanently?</Text>
          </View>
        }
        actionButtons={[
          {
            text: 'Delete',
            width: 100,
            onPress: handleDeleteDocument,
          },
          {
            text: 'Cancel',
            width: 100,
            onPress: () => setShowDeleteDocModal(false),
          },
        ]}
        closePopup={() => setShowDeleteDocModal(false)}
      />
    </ScrollView>
  );
};

export default drawerHoc({
  Component: ArchiveManager,
  showBackButton: true,
  backNavigation: 'Setting',
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    marginBottom: 18,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  /* SUB-NAV TABS */
  tabsRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: 20,
    gap: 24,
  },
  tabItem: {
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabItemActive: {
    borderBottomColor: '#6C2BD9',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  tabTextActive: {
    color: '#6C2BD9',
    fontWeight: '700',
  },

  /* CONTROLS ROW */
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  controlsRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },

  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    height: 40,
    ...SHADOWS.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    outlineStyle: 'none',
  },

  sortBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    height: 40,
    ...SHADOWS.sm,
  },
  sortBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },

  /* CARD CONTAINER */
  cardContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 16,
    ...SHADOWS.sm,
    marginBottom: 40,
  },
  cardContainerMobile: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    padding: 0,
    shadowColor: 'transparent',
  },

  /* TABLE VIEW */
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  headerCell: {
    flex: 1,
    fontSize: 10,
    fontWeight: '700',
    color: '#9CA3AF',
    letterSpacing: 0.5,
  },

  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
  },

  titleLeftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1.5,
  },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  itemTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },

  dataCell: {
    flex: 1,
    fontSize: 13,
    color: '#374151',
  },

  amberBadge: {
    backgroundColor: '#FEF3C7',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
    alignSelf: 'flex-start',
  },
  amberBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#D97706',
  },

  actionsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
    gap: 12,
  },
  actionIconButton: {
    padding: 4,
  },

  /* MOBILE CARD */
  mobileCard: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 14,
    marginBottom: 12,
    ...SHADOWS.sm,
  },
  mobileActionsRow: {
    width: '100%',
    justifyContent: 'flex-end',
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
    paddingTop: 10,
    marginTop: 8,
  },
  mobileCellText: {
    fontSize: 12,
    color: '#374151',
    marginVertical: 2,
  },
  mobileLabelText: {
    fontWeight: '600',
    color: '#6B7280',
  },

  /* EMPTY BOX */
  emptyBox: {
    padding: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#6B7280',
  },

  /* FIGMA EMPTY STATE FOR TENANTS */
  emptyBoxFigma: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyCircleIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyCircleText: {
    fontSize: 22,
    fontWeight: '700',
    color: '#6C2BD9',
  },
  emptyFigmaTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 6,
  },
  emptyFigmaDesc: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    maxWidth: 340,
    lineHeight: 18,
    marginBottom: 20,
  },
  viewTenanciesBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 20,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewTenanciesBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
